import type { ProductRecord, StockQuantRecord } from "@stockpilot/powersync";
import { formatQty } from "../inventory/units";
import { buildDataset, type ExportColumn, type ExportDataset, type ExportMetadata } from "./types";

/**
 * Stockpilot adapters (K1): map inventory records into the generic
 * {@link ExportDataset}. These are the ONLY domain-specific pieces — the
 * CSV/PDF generators never learn about products or stock.
 *
 * Booleans stored as 0/1 integers in SQLite are normalized to "Yes"/"No";
 * date columns pass through as ISO strings (the consumer can localize).
 * Quantities stored in minor units are rendered with {@link formatQty} so the
 * exported number matches what the UI shows.
 */

function yesNo(value: number | null | undefined): string {
  return value === 1 ? "Yes" : "No";
}

// ── Products ────────────────────────────────────────────────────────────────

export const PRODUCT_EXPORT_COLUMNS: ExportColumn<ProductRecord>[] = [
  { key: "name", header: "Name", accessor: (p) => p.name ?? "" },
  { key: "sku", header: "SKU", accessor: (p) => p.sku ?? "" },
  { key: "barcode", header: "Barcode", accessor: (p) => p.barcode ?? "" },
  { key: "tracking", header: "Tracking", accessor: (p) => p.tracking ?? "none" },
  { key: "active", header: "Active", accessor: (p) => yesNo(p.active) },
  { key: "created_at", header: "Created", accessor: (p) => p.created_at ?? "" },
];

/** Builds an {@link ExportDataset} from the product catalog. */
export function buildProductDataset(
  products: readonly ProductRecord[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(PRODUCT_EXPORT_COLUMNS, products, {
    title: "Products",
    ...metadata,
  });
}

// ── Stock on-hand ───────────────────────────────────────────────────────────

/**
 * `stock_quant` rows only carry foreign keys; the caller supplies resolvers
 * (typically memoized id→name maps already built for the on-hand table) so the
 * export shows human-readable product/location names.
 */
export interface StockExportResolvers {
  productName: (id: string) => string;
  locationName: (id: string) => string;
}

export function stockExportColumns(
  resolve: StockExportResolvers,
): ExportColumn<StockQuantRecord>[] {
  return [
    { key: "product", header: "Product", accessor: (q) => resolve.productName(q.product_id ?? "") },
    {
      key: "location",
      header: "Location",
      accessor: (q) => resolve.locationName(q.location_id ?? ""),
    },
    { key: "lot", header: "Lot", accessor: (q) => q.lot_id ?? "" },
    // Minor units → UI quantity, same rendering as the on-hand table.
    { key: "qty", header: "On-hand", accessor: (q) => formatQty(q.qty ?? 0) },
  ];
}

/** Builds an {@link ExportDataset} from on-hand quants + name resolvers. */
export function buildStockDataset(
  quants: readonly StockQuantRecord[],
  resolve: StockExportResolvers,
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(stockExportColumns(resolve), quants, {
    title: "Stock on-hand",
    ...metadata,
  });
}
